/**
 * TRUS-762 (proposal 5) — the member Forms hub.
 *
 * One view per member + program (enrollment) answering three questions:
 *
 *   1. To complete    — this enrollment's unfinished form instances, the current
 *                       user's first, plus the program's REQUIRED templates that
 *                       have no instance yet ("Required · not started").
 *   2. Start a new form — templates for THIS program only (GET /by-program/{id}),
 *                       required first, then grouped by form type, with search.
 *   3. Completed      — submitted / reviewed / approved / billed instances.
 *
 * No form configuration or backend change: it reads the same endpoints the
 * Form Library does, creates instances through the same create call (including
 * the "required inputs" prompt), and opens them in the same FormInstanceEditor.
 *
 * Mounting it: pass `onOpenInstance` / `onStartTemplate` to route opening and
 * starting through the host (e.g. the workspace's own panel). Leave them out
 * and the hub handles both itself, opening FormInstanceEditor in place.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Eye,
  FilePlus2,
  FileText,
  Pin,
  Play,
  Search,
  Star,
} from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import useFormInstances from '@/hooks/useFormInstances';
import useFormTemplates from '@/hooks/useFormTemplates';
import { formsAPI } from '@/services/api/forms';
import { enrollmentsAPI, type Enrollment } from '@/services/api/enrollments';
import { templateVariablesApi, type RequiredInput } from '@/components/messaging/templateVariables';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useOptionalAuth } from '@/contexts/AuthContext';
import { useFormattedDate } from '@/hooks/useUserTimezone';

import { FormInstanceEditor } from './FormInstanceEditor';
import { FormStartInputsDialog } from './FormStartInputsDialog';
import { bucketMemberForms, type ToCompleteRow } from './memberFormsHubModel';
import {
  FORMS_COUNT_PILL,
  FORMS_MUTED,
  FORMS_PRIMARY_BTN,
  FORMS_SECONDARY_BTN,
  FORMS_SURFACE,
  FORMS_TABLE_HEAD,
} from './formsUi';

export interface MemberFormsHubProps {
  memberId: number;
  /** The program whose templates are offered. Derived from the enrollment when omitted. */
  programId?: number;
  /** The enrollment whose forms are listed. When omitted the hub resolves it (picker if >1). */
  enrollmentId?: number;
  /** New instances are filed under this case. */
  caseEpisodeId?: number | null;
  /** Organization id, forwarded to the in-place editor (billing capture). */
  organizationId?: number;
  /** Open an existing instance. Omit to open it in place with FormInstanceEditor. */
  onOpenInstance?: (instanceId: number) => void;
  /**
   * Start a template for an enrollment. Omit to create the instance here (same
   * create call + required-inputs prompt as the Form Library) and open it in place.
   */
  onStartTemplate?: (templateId: number, enrollmentId: number) => void;
  /**
   * TRUS-762 round 4: show only this template — its open drafts to continue and
   * its Start — e.g. when opened from a form pinned to the member sidebar. The
   * user can widen back to every form.
   */
  focusTemplateId?: number;
  /** Label for the focused form (the pin's label); falls back to the template name. */
  focusLabel?: string;
}

/** HTTP statuses whose `detail` is written for the person who clicked (TRUS-762). */
const ACTIONABLE_STATUSES = [400, 403, 404, 409, 422];

/**
 * The message to show when Start fails. The hub used to say "Could not start
 * this form" for everything, which hid the one thing staff could act on — a
 * wrong organization, a case that belongs to another member, no permission.
 * A 4xx with a plain-text `detail` is shown as the server wrote it; anything
 * else (5xx, validation arrays, no response) keeps the generic text.
 */
export function startErrorMessage(err: unknown, generic: string, offline: string): string {
  const e = err as { response?: { status?: number; data?: { detail?: unknown } }; request?: unknown };
  if (!e?.response) return e?.request ? offline : generic;
  const status = e.response.status ?? 0;
  const detail = e.response.data?.detail;
  if (ACTIONABLE_STATUSES.includes(status) && typeof detail === 'string' && detail.trim()) return detail.trim();
  return generic;
}

const STATUS_FALLBACK: Record<string, string> = {
  draft: 'Draft',
  in_progress: 'In progress',
  pending_signature: 'Pending signature',
  pending_review: 'Pending review',
  submitted: 'Submitted',
  reviewed: 'Reviewed',
  approved: 'Approved',
  completed: 'Completed',
  signed: 'Signed',
  billed: 'Billed',
  billed_qa: 'Billed + QA',
  locked: 'Locked',
};

const TYPE_FALLBACK: Record<string, string> = {
  intake: 'Intake',
  consent: 'Consent',
  assessment: 'Assessment',
  care_plan: 'Care plan',
  progress_note: 'Progress note',
  billing: 'Billing',
  custom: 'Other forms',
  event_detail: 'Event detail',
  activity_log: 'Activity log',
};

const humanize = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ') : '';

export const MemberFormsHub: React.FC<MemberFormsHubProps> = ({
  memberId,
  programId,
  enrollmentId,
  caseEpisodeId,
  organizationId,
  onOpenInstance,
  onStartTemplate,
  focusTemplateId,
  focusLabel,
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const auth = useOptionalAuth();
  const currentUserId = auth?.user?.id ?? null;

  const statusLabel = (status: string) => {
    const key = (status || '').toLowerCase();
    return t(`formsHub.status.${key}`, STATUS_FALLBACK[key] || humanize(key));
  };
  const typeLabel = (formType: string) => {
    const key = (formType || 'custom').toLowerCase();
    return t(`formsHub.type.${key}`, TYPE_FALLBACK[key] || humanize(key));
  };

  // ── Resolve the enrollment (and program) ────────────────────────────────
  const needsEnrollmentLookup = !enrollmentId || !programId;
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(needsEnrollmentLookup);
  const [pickedEnrollmentId, setPickedEnrollmentId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!needsEnrollmentLookup || !memberId) {
      setEnrollmentsLoading(false);
      return;
    }
    let cancelled = false;
    setEnrollmentsLoading(true);
    enrollmentsAPI
      .listEnrollments({ member_id: memberId, page_size: 100 })
      .then((res) => {
        if (!cancelled) setEnrollments(Array.isArray(res?.enrollments) ? res.enrollments : []);
      })
      .catch(() => {
        if (!cancelled) setEnrollments([]);
      })
      .finally(() => {
        if (!cancelled) setEnrollmentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId, needsEnrollmentLookup]);

  const resolvedEnrollmentId: number | undefined = useMemo(() => {
    if (enrollmentId) return enrollmentId;
    if (pickedEnrollmentId) return pickedEnrollmentId;
    if (programId) {
      const forProgram = enrollments.filter((e) => e.program_id === programId);
      if (forProgram.length >= 1) return forProgram[0].id;
    }
    return enrollments.length === 1 ? enrollments[0].id : undefined;
  }, [enrollmentId, pickedEnrollmentId, programId, enrollments]);

  const resolvedProgramId: number | undefined =
    programId ?? enrollments.find((e) => e.id === resolvedEnrollmentId)?.program_id;

  // The picker appears only when nothing was passed in and the member has more than one.
  const showPicker = !enrollmentId && !programId && enrollments.length > 1;

  // ── Data ─────────────────────────────────────────────────────────────────
  const {
    instances,
    loading: instancesLoading,
    error: instancesError,
    refetch: refetchInstances,
  } = useFormInstances({
    enrollment_id: resolvedEnrollmentId,
    autoFetch: !!resolvedEnrollmentId,
    fetchAll: true,
  });

  const {
    templates,
    loading: templatesLoading,
    error: templatesError,
  } = useFormTemplates({
    program_id: resolvedProgramId,
    is_active: true,
    programScoped: true,
    autoFetch: !!resolvedProgramId,
  });

  const [search, setSearch] = useState('');
  // Focus (a pinned form): narrow both lists to the one template until the user widens.
  const [focusCleared, setFocusCleared] = useState(false);
  const focusId = focusTemplateId && !focusCleared ? focusTemplateId : undefined;
  const focusedTemplate = focusId ? templates.find((tpl) => tpl.id === focusId) : undefined;
  const buckets = useMemo(() => {
    const scopedInstances = focusId ? instances.filter((i) => i.form_template_id === focusId) : instances;
    const scopedTemplates = focusId ? templates.filter((tpl) => tpl.id === focusId) : templates;
    return bucketMemberForms({ instances: scopedInstances, templates: scopedTemplates, currentUserId, search });
  }, [instances, templates, currentUserId, search, focusId]);

  // ── Open / start (fallbacks when the host does not route them) ──────────
  const [editingInstanceId, setEditingInstanceId] = useState<number | null>(null);
  const [pendingStart, setPendingStart] = useState<{ templateId: number; inputs: RequiredInput[] } | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [startingId, setStartingId] = useState<number | null>(null);

  const openInstance = (id: number) => {
    if (onOpenInstance) onOpenInstance(id);
    else setEditingInstanceId(id);
  };

  const createAndOpen = async (templateId: number, variableInputs?: Record<string, string> | null) => {
    if (!resolvedEnrollmentId) return;
    setStartError(null);

    if (variableInputs === undefined) {
      try {
        const { required_inputs } = await templateVariablesApi.requiredInputs({ form_template_id: templateId });
        if (required_inputs && required_inputs.length > 0) {
          setPendingStart({ templateId, inputs: required_inputs });
          return;
        }
      } catch {
        // No registry answer: start the form; unfillable fields are left empty.
      }
    }

    setStartingId(templateId);
    try {
      // Called directly rather than through useFormInstances.createInstance,
      // which swallows the error into hook state and returns null — that is how
      // every failure became one generic message (TRUS-762).
      const created = await formsAPI.createFormInstance({
        form_template_id: templateId,
        enrollment_id: resolvedEnrollmentId,
        ...(caseEpisodeId ? { case_episode_id: caseEpisodeId } : {}),
        ...(variableInputs ? { variable_inputs: variableInputs } : {}),
      });
      void refetchInstances();
      openInstance(created.id);
    } catch (err) {
      console.error('Start form failed:', err);
      setStartError(
        startErrorMessage(
          err,
          t('formsHub.start_failed', 'Could not start this form. Please try again; if it keeps failing, contact support.'),
          t('formsHub.start_offline', 'Could not reach the server to start this form. Check your connection and try again.'),
        ),
      );
    } finally {
      setStartingId(null);
    }
  };

  const startTemplate = (templateId: number) => {
    if (!resolvedEnrollmentId) return;
    if (onStartTemplate) onStartTemplate(templateId, resolvedEnrollmentId);
    else void createAndOpen(templateId);
  };

  if (editingInstanceId) {
    return (
      <FormInstanceEditor
        instanceId={editingInstanceId}
        memberId={memberId}
        programId={resolvedProgramId}
        organizationId={organizationId}
        onClose={() => {
          setEditingInstanceId(null);
          void refetchInstances();
        }}
      />
    );
  }

  // ── Rendering ────────────────────────────────────────────────────────────
  const sectionHeader = (icon: React.ReactNode, title: string, count?: number, hint?: string) => (
    <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-v2border">
      <div className="min-w-0">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
          {icon}
          {title}
          {count !== undefined && <span className={FORMS_COUNT_PILL}>{count}</span>}
        </h3>
        {hint && <p className={`mt-0.5 text-xs ${FORMS_MUTED}`}>{hint}</p>}
      </div>
    </div>
  );

  const loadingBlock = (
    <div className="flex justify-center py-8">
      <LoadingSpinner size="md" />
    </div>
  );

  const emptyLine = (text: string) => <p className={`px-4 py-6 text-sm text-center ${FORMS_MUTED}`}>{text}</p>;

  const renderToCompleteRow = (row: ToCompleteRow) => {
    if (row.kind === 'required') {
      const tpl = row.template;
      return (
        <li key={`req-${tpl.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
          <FileText className="w-4 h-4 shrink-0 text-ink-500" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-ink truncate">{tpl.name}</p>
            <p className={`text-xs ${FORMS_MUTED}`}>{typeLabel(tpl.form_type)}</p>
          </div>
          <Badge variant="warning" size="sm">
            {t('formsHub.required_not_started', 'Required · not started')}
          </Badge>
          <Button
            size="sm"
            variant="default"
            className={FORMS_PRIMARY_BTN}
            disabled={startingId !== null}
            onClick={() => startTemplate(tpl.id)}
          >
            <Play className="w-3.5 h-3.5" />
            {t('formsHub.start', 'Start')}
          </Button>
        </li>
      );
    }

    const { instance, mine, overdue } = row;
    const pct = Math.round(Number(instance.completion_percentage) || 0);
    return (
      <li key={`inst-${instance.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
        <ClipboardList className="w-4 h-4 shrink-0 text-ink-500" />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-sm font-medium text-ink">
            <span className="truncate">{instance.form_template_name}</span>
            {mine && (
              <span className="inline-flex items-center rounded-full px-1.5 text-[11px] font-medium bg-v2accent-soft text-v2accent-ink">
                {t('formsHub.mine', 'Assigned to you')}
              </span>
            )}
          </p>
          <p className={`flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs ${FORMS_MUTED}`}>
            {!mine && instance.assigned_to_name && (
              <span>{t('formsHub.assigned_to', 'Assigned to {{name}}', { name: instance.assigned_to_name })}</span>
            )}
            {instance.due_date && (
              <span className={overdue ? 'inline-flex items-center gap-1 font-medium text-danger-ink' : ''}>
                {overdue && <AlertTriangle className="w-3 h-3" />}
                {overdue
                  ? t('formsHub.overdue_since', 'Overdue · due {{date}}', { date: fmt.short(instance.due_date) })
                  : t('formsHub.due_on', 'Due {{date}}', { date: fmt.short(instance.due_date) })}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 w-28" title={t('formsHub.completion', 'Completion')}>
          <div className="h-1.5 flex-1 rounded-full bg-paper-2 overflow-hidden">
            <div className="h-full rounded-full bg-v2accent" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
          </div>
          <span className={`text-xs tabular-nums ${FORMS_MUTED}`}>{pct}%</span>
        </div>
        <Badge variant={overdue ? 'danger' : 'default'} size="sm">
          {statusLabel(instance.status)}
        </Badge>
        <Button
          size="sm"
          variant="default"
          className={FORMS_PRIMARY_BTN}
          onClick={() => openInstance(instance.id)}
        >
          {t('formsHub.continue', 'Continue')}
        </Button>
      </li>
    );
  };

  if (enrollmentsLoading) return loadingBlock;

  if (!resolvedEnrollmentId && !showPicker) {
    return (
      <Card className={`p-6 text-center ${FORMS_SURFACE}`}>
        <p className={`text-sm ${FORMS_MUTED}`}>
          {t('formsHub.no_enrollment', 'This member has no program enrollment, so there are no forms to show yet.')}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {pendingStart && (
        <FormStartInputsDialog
          inputs={pendingStart.inputs}
          onCancel={() => setPendingStart(null)}
          onConfirm={(values) => {
            const { templateId } = pendingStart;
            setPendingStart(null);
            void createAndOpen(templateId, values ?? null);
          }}
        />
      )}

      {showPicker && (
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-sm ${FORMS_MUTED}`}>{t('formsHub.enrollment', 'Enrollment')}</span>
          <div className="w-72 max-w-full">
            <Select
              value={resolvedEnrollmentId ? String(resolvedEnrollmentId) : ''}
              onValueChange={(v) => setPickedEnrollmentId(Number(v))}
            >
              <SelectTrigger aria-label={t('formsHub.enrollment', 'Enrollment')}>
                <SelectValue placeholder={t('formsHub.pick_enrollment', 'Choose a program enrollment…')} />
              </SelectTrigger>
              <SelectContent>
                {enrollments.map((e) => (
                  <SelectItem key={e.id} value={String(e.id)}>
                    {e.program_name || t('formsHub.enrollment_n', 'Enrollment #{{id}}', { id: e.id })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {focusTemplateId && !focusCleared && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-v2border bg-surface px-3 py-2"
          data-testid="forms-hub-focus"
        >
          <span className="flex items-center gap-2 text-sm text-ink">
            <Pin className="w-3.5 h-3.5 text-v2accent" />
            {t('formsHub.focus_showing', 'Showing {{name}} only', {
              name: focusLabel || focusedTemplate?.name || t('formsHub.focus_this_form', 'this form'),
            })}
            {!templatesLoading && !focusedTemplate && (
              <span className="text-ink-500" data-testid="forms-hub-focus-missing">
                {t('formsHub.focus_not_in_program', '— this form is not set up for this program.')}
              </span>
            )}
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={() => setFocusCleared(true)}>
            {t('formsHub.focus_show_all', 'Show all forms')}
          </Button>
        </div>
      )}

      {(instancesError || templatesError || startError) && (
        <Alert variant="destructive">
          <AlertDescription>{startError || instancesError || templatesError}</AlertDescription>
        </Alert>
      )}

      {!resolvedEnrollmentId ? (
        <Card className={`p-6 text-center ${FORMS_SURFACE}`}>
          <p className={`text-sm ${FORMS_MUTED}`}>
            {t('formsHub.pick_enrollment_hint', 'Choose an enrollment to see its forms.')}
          </p>
        </Card>
      ) : (
        <>
          {/* 1 — To complete */}
          <Card className={FORMS_SURFACE} data-testid="forms-hub-to-complete">
            {sectionHeader(
              <ClipboardList className="w-4 h-4 text-v2accent" />,
              t('formsHub.to_complete', 'To complete'),
              buckets.toComplete.length,
            )}
            {instancesLoading || templatesLoading ? (
              loadingBlock
            ) : buckets.toComplete.length === 0 ? (
              emptyLine(t('formsHub.nothing_to_complete', 'Nothing waiting — every form here is finished.'))
            ) : (
              <ul className="divide-y divide-v2border">{buckets.toComplete.map(renderToCompleteRow)}</ul>
            )}
          </Card>

          {/* 2 — Start a new form */}
          <Card className={FORMS_SURFACE} data-testid="forms-hub-start">
            {sectionHeader(
              <FilePlus2 className="w-4 h-4 text-v2accent" />,
              t('formsHub.start_new', 'Start a new form'),
              undefined,
              t('formsHub.start_new_hint', 'Forms available for this program.'),
            )}
            <div className="px-4 pt-3">
              <div className="relative max-w-sm">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500 pointer-events-none" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('formsHub.search_templates', 'Search forms…')}
                  aria-label={t('formsHub.search_templates', 'Search forms…')}
                  className="pl-8 bg-surface border-v2border"
                />
              </div>
            </div>
            {templatesLoading ? (
              loadingBlock
            ) : buckets.startRequired.length === 0 && buckets.startGroups.length === 0 ? (
              emptyLine(
                search
                  ? t('formsHub.no_template_matches', 'No forms match your search.')
                  : t('formsHub.no_templates', 'No forms are set up for this program.'),
              )
            ) : (
              <div className="px-4 py-3 space-y-4">
                {buckets.startRequired.length > 0 && (
                  <div>
                    <p className={`mb-1.5 flex items-center gap-1 ${FORMS_TABLE_HEAD}`}>
                      <Star className="w-3 h-3" />
                      {t('formsHub.required_for_program', 'Required for this program')}
                    </p>
                    <ul className="divide-y divide-v2border rounded-md border border-v2border">
                      {buckets.startRequired.map((tpl) => (
                        <li key={tpl.id} className="flex items-center gap-3 px-3 py-2">
                          <span className="min-w-0 flex-1 truncate text-sm text-ink">{tpl.name}</span>
                          <span className={`text-xs ${FORMS_MUTED}`}>{typeLabel(tpl.form_type)}</span>
                          <Button
                            size="sm"
                            variant="secondary"
                            className={FORMS_SECONDARY_BTN}
                            disabled={startingId !== null}
                            onClick={() => startTemplate(tpl.id)}
                          >
                            <Play className="w-3.5 h-3.5" />
                            {t('formsHub.start', 'Start')}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {buckets.startGroups.map((group) => (
                  <div key={group.formType}>
                    <p className={`mb-1.5 ${FORMS_TABLE_HEAD}`}>{typeLabel(group.formType)}</p>
                    <ul className="divide-y divide-v2border rounded-md border border-v2border">
                      {group.templates.map((tpl) => (
                        <li key={tpl.id} className="flex items-center gap-3 px-3 py-2">
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm text-ink">{tpl.name}</span>
                            {tpl.description && (
                              <span className={`block truncate text-xs ${FORMS_MUTED}`}>{tpl.description}</span>
                            )}
                          </span>
                          <Button
                            size="sm"
                            variant="secondary"
                            className={FORMS_SECONDARY_BTN}
                            disabled={startingId !== null}
                            onClick={() => startTemplate(tpl.id)}
                          >
                            <Play className="w-3.5 h-3.5" />
                            {t('formsHub.start', 'Start')}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* 3 — Completed */}
          <Card className={FORMS_SURFACE} data-testid="forms-hub-completed">
            {sectionHeader(
              <CheckCircle2 className="w-4 h-4 text-v2accent" />,
              t('formsHub.completed', 'Completed'),
              buckets.completed.length,
            )}
            {instancesLoading ? (
              loadingBlock
            ) : buckets.completed.length === 0 ? (
              emptyLine(t('formsHub.none_completed', 'No completed forms yet.'))
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-v2border">
                    <TableHead className={FORMS_TABLE_HEAD}>{t('formsHub.col_form', 'Form')}</TableHead>
                    <TableHead className={FORMS_TABLE_HEAD}>{t('formsHub.col_submitted', 'Submitted')}</TableHead>
                    <TableHead className={FORMS_TABLE_HEAD}>{t('formsHub.col_status', 'Status')}</TableHead>
                    <TableHead className={FORMS_TABLE_HEAD}>{t('formsHub.col_by', 'By')}</TableHead>
                    <TableHead className={`${FORMS_TABLE_HEAD} text-right`}>
                      <span className="sr-only">{t('formsHub.col_actions', 'Actions')}</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {buckets.completed.map((inst) => (
                    <TableRow key={inst.id} className="border-v2border">
                      <TableCell className="font-medium text-ink">{inst.form_template_name}</TableCell>
                      <TableCell className={`whitespace-nowrap ${FORMS_MUTED}`}>
                        {inst.submitted_at ? fmt.short(inst.submitted_at) : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="success" size="sm">
                          {statusLabel(inst.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className={FORMS_MUTED}>{inst.assigned_to_name || '—'}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="secondary"
                          className={FORMS_SECONDARY_BTN}
                          onClick={() => openInstance(inst.id)}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          {t('formsHub.view', 'View')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default MemberFormsHub;
